"use client";

import { AnimatePresence, m } from "motion/react";
import { type ComponentProps, useState } from "react";
import DemoCheckbox from "@/components/preview/demo-checkbox";
import DemoFooter from "@/components/preview/demo-footer";
import DemoPanel from "@/components/preview/demo-panel";
import DemoSplit from "@/components/preview/demo-split";
import DemoWrapper from "@/components/preview/demo-wrapper";
import { ResultBadge } from "@/components/ui/result-badge";
import { SPRING } from "@/lib/motion";
import { cn } from "@/lib/utils";

type ConcentricBorderRadiusDemoProps = Pick<
  ComponentProps<typeof DemoWrapper>,
  "captionText" | "height" | "margin"
> & { defaultShowValues?: boolean };

function RadiusArc({
  radius,
  strokeWidth = 1,
  extension = 8,
  corner = "top-left",
  className,
}: {
  radius: number;
  strokeWidth?: number;
  extension?: number;
  corner?: "top-left" | "top-right";
  className?: string;
}) {
  const size = radius + strokeWidth + extension;
  const offset = strokeWidth / 2;

  const path =
    corner === "top-left"
      ? `M ${offset} ${radius + offset + extension} L ${offset} ${radius + offset} A ${radius} ${radius} 0 0 1 ${radius + offset} ${offset} L ${radius + offset + extension} ${offset}`
      : `M ${size - radius - offset - extension} ${offset} L ${size - radius - offset} ${offset} A ${radius} ${radius} 0 0 1 ${size - offset} ${radius + offset} L ${size - offset} ${radius + offset + extension}`;

  return (
    <svg
      aria-hidden="true"
      className={cn(
        "pointer-events-none absolute -top-px",
        corner === "top-left" ? "-left-px" : "-right-px",
        className
      )}
      fill="none"
      height={size}
      role="graphics-symbol"
      viewBox={`0 0 ${size} ${size}`}
      width={size}
    >
      <path d={path} strokeLinecap="round" strokeWidth={strokeWidth} />
    </svg>
  );
}

function ValueLabel({
  children,
  className,
  show,
  layoutKey,
}: {
  children: React.ReactNode;
  className: string;
  show: boolean;
  layoutKey: string;
}) {
  return (
    <AnimatePresence initial={false}>
      {show ? (
        <m.div
          animate={{ filter: "blur(0px)", opacity: 1, scale: 1 }}
          className={cn(
            "absolute flex h-6 w-12 select-none items-center justify-center rounded-full font-berkeley-mono font-medium text-xs will-change-transform",
            className
          )}
          exit={{ filter: "blur(2px)", opacity: 0, scale: 0.9 }}
          initial={{ filter: "blur(2px)", opacity: 0, scale: 0.9 }}
          key={layoutKey}
          transition={SPRING}
        >
          {children}
        </m.div>
      ) : null}
    </AnimatePresence>
  );
}

function ArcOverlay({
  show,
  layoutKey,
  children,
  className,
}: {
  show: boolean;
  layoutKey: string;
  children: React.ReactNode;
  className: string;
}) {
  return (
    <AnimatePresence initial={false}>
      {show ? (
        <m.div
          animate={{ opacity: 1 }}
          className={cn("absolute will-change-transform", className)}
          exit={{ opacity: 0 }}
          initial={{ opacity: 0 }}
          key={layoutKey}
          transition={SPRING}
        >
          {children}
        </m.div>
      ) : null}
    </AnimatePresence>
  );
}

export default function ConcentricBorderRadiusDemo({
  defaultShowValues = true,
  ...props
}: ConcentricBorderRadiusDemoProps) {
  const [showValues, setShowValues] = useState(defaultShowValues);

  return (
    <DemoWrapper className="shrink-0" {...props}>
      <DemoSplit className="min-h-0 grow" stackBelow="sm">
        <DemoPanel innerClassName="flex-col gap-4 px-4 py-8">
          <div className="relative flex items-center justify-center rounded-xl bg-preview-bg p-2 shadow-custom">
            <ArcOverlay
              className="top-0 left-0"
              layoutKey="outer-arc-bad"
              show={showValues}
            >
              <RadiusArc
                className="stroke-gray-900"
                extension={16}
                radius={12}
              />
            </ArcOverlay>
            <ValueLabel
              className="-top-6 -left-12 border border-gray-400 bg-preview-bg text-gray-1200"
              layoutKey="outer-label-bad"
              show={showValues}
            >
              12px
            </ValueLabel>
            <div className="relative flex size-20 items-center justify-center rounded-xl border border-gray-400 bg-gray-200 p-2 sm:size-32">
              <ArcOverlay
                className="top-0 left-0"
                layoutKey="inner-arc-bad"
                show={showValues}
              >
                <RadiusArc className="stroke-gray-900" radius={12} />
              </ArcOverlay>
              <ValueLabel
                className="top-2 left-2 bg-preview-bg shadow-custom"
                layoutKey="inner-label-bad"
                show={showValues}
              >
                12px
              </ValueLabel>
            </div>
          </div>
          <ResultBadge bad />
        </DemoPanel>

        <DemoPanel innerClassName="flex-col gap-4 px-4 py-8">
          <div className="relative flex items-center justify-center rounded-[20px] bg-preview-bg p-2 shadow-custom">
            <ArcOverlay
              className="top-0 right-0"
              layoutKey="outer-arc-good"
              show={showValues}
            >
              <RadiusArc
                className="stroke-sky-500"
                corner="top-right"
                radius={20}
              />
            </ArcOverlay>
            <ValueLabel
              className="-top-6 -right-11 border border-sky-500 bg-sky-500/5 text-sky-500"
              layoutKey="outer-label-good"
              show={showValues}
            >
              20px
            </ValueLabel>
            <div className="relative size-20 rounded-xl border border-gray-400 bg-gray-200 sm:size-32">
              <ArcOverlay
                className="top-0 right-0"
                layoutKey="inner-arc-good"
                show={showValues}
              >
                <RadiusArc
                  className="stroke-gray-900"
                  corner="top-right"
                  radius={12}
                />
              </ArcOverlay>
              <ValueLabel
                className="top-2 right-2 bg-preview-bg shadow-custom"
                layoutKey="inner-label-good"
                show={showValues}
              >
                12px
              </ValueLabel>
            </div>
          </div>
          <ResultBadge />
        </DemoPanel>
      </DemoSplit>
      <DemoFooter>
        <DemoCheckbox
          checked={showValues}
          label="Show values"
          onCheckedChange={setShowValues}
        />
      </DemoFooter>
    </DemoWrapper>
  );
}
